import type { ProductAccessLevels } from '../types'

/** The grant a channel carries when a member has no stored row for it. */
export const NO_ACCESS: ProductAccessLevels = {
  publish: 'none',
  comments: 'none',
}

type ChannelGrant = {
  channel: { id: string }
  productAccessLevels: ProductAccessLevels
}

/**
 * One per-product grant for every assignable channel, in that order, with
 * channels the member has no stored row for reading as no access. This is
 * the shape both the edit dialog's per-channel form and the "Customized"
 * check compare, so the two always agree on what varies.
 */
export const buildPerChannelGrants = (
  channelPermissions: readonly ChannelGrant[],
  assignableChannelIds: readonly string[],
): Map<string, ProductAccessLevels> => {
  const grantByChannel = new Map(
    channelPermissions.map((permission) => [
      permission.channel.id,
      permission.productAccessLevels,
    ]),
  )
  return new Map(
    assignableChannelIds.map((channelId) => [
      channelId,
      grantByChannel.get(channelId) ?? NO_ACCESS,
    ]),
  )
}

/**
 * Whether two per-product grants are identical across every product.
 */
export const areProductLevelsEqual = (
  a: ProductAccessLevels,
  b: ProductAccessLevels,
): boolean => a.publish === b.publish && a.comments === b.comments

/**
 * Whether every channel shares the same per-product grant — i.e. there is no
 * variance to display. Drives the Team Member edit modal's initial state: a
 * uniform member opens in the collapsed "applies to all" view; any variance
 * (a channel differing on either product) opens in the expanded per-channel
 * table so the existing mix isn't silently misrepresented.
 *
 * An empty list is treated as uniform (nothing varies).
 */
export const areAllProductLevelsUniform = (
  levels: ProductAccessLevels[],
): boolean => {
  const [first, ...rest] = levels
  if (!first) return true
  return rest.every((level) => areProductLevelsEqual(level, first))
}
